import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { retentionFor } from '@content-engine/shared';
import {
  archiveEntity,
  fetchAccounts,
  fetchVariant,
  regenerateVariantApi,
  shareTelegramApi,
  unarchiveEntity,
  updateVariantApi,
} from '../../lib/queries.ts';
import {
  ArchiveDialog,
  ArchivedChip,
  Capability,
  CapabilityBlock,
  FileChip,
  MediaPlaceholder,
  ParentLink,
  formatBytes,
  isAvailable,
  isFreed,
  shortDate,
} from '../../components/archive/index.ts';
import { variantTitle } from './title.ts';

/**
 * Карточка варианта (прототип §6.2).
 *
 * Вариант САМОДОСТАТОЧЕН: он публикуется, даже если клип и фильм в архиве.
 * Архивность родителя не отнимает у него ничего — способности отнимает только
 * освобождение файлов (своего и foundation клипа).
 */
export function VariantDetailPage(): React.ReactElement {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [accountId, setAccountId] = useState('');

  const variantQuery = useQuery({
    queryKey: ['clipVariant', id],
    queryFn: () => fetchVariant(id),
    enabled: id !== '',
    refetchInterval: (q) => {
      const s = q.state.data?.variant.status;
      return s === 'pending' || s === 'rendering' ? 3_000 : false;
    },
  });

  // Публикация варианта — существующая ручка шаринга клипа в Telegram
  // (POST /clips/:id/share/telegram с variantId). Отдельного «опубликовать
  // вариант» на бэкенде нет.
  const tgAccountsQuery = useQuery({
    queryKey: ['accounts', 'telegram'],
    queryFn: () => fetchAccounts({ platform: 'telegram', status: 'active' }),
    staleTime: 60_000,
  });
  const tgAccounts = tgAccountsQuery.data?.accounts ?? [];

  useEffect(() => {
    if (!accountId && tgAccounts.length > 0) {
      const first = tgAccounts[0];
      if (first) setAccountId(first.id);
    }
  }, [tgAccounts, accountId]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['clipVariant', id] });
    void queryClient.invalidateQueries({ queryKey: ['variants'] });
    void queryClient.invalidateQueries({ queryKey: ['clips'] });
  };

  const archiveMutation = useMutation({
    mutationFn: (opts: { freeFiles: boolean }) => archiveEntity('clip_variant', id, opts),
    onSuccess: () => {
      setShowArchiveDialog(false);
      invalidate();
    },
    onError: (error: Error) => {
      setShowArchiveDialog(false);
      alert(error.message);
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveEntity('clip_variant', id),
    onSuccess: invalidate,
    onError: (error: Error) => alert(error.message),
  });

  // Модерация и пересборка — те же ручки, что жили в лайтбоксе на странице
  // фильма. Карточка варианта — теперь его единственный полноценный дом.
  const moderateMutation = useMutation({
    mutationFn: (patch: { status: 'approved' | 'rejected'; rejectedReason?: string }) =>
      updateVariantApi(id, patch),
    onSuccess: invalidate,
    onError: (error: Error) => alert(error.message),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => regenerateVariantApi(id),
    onSuccess: invalidate,
    onError: (error: Error) => alert(error.message),
  });

  const data = variantQuery.data;

  const shareMutation = useMutation({
    mutationFn: () =>
      data && accountId
        ? shareTelegramApi(data.variant.clipId, { accountId, variantId: data.variant.id })
        : Promise.reject(new Error('no variant or account')),
    onSuccess: (result) => {
      setShowPublish(false);
      alert(t('clips.shareTelegramSuccess', { messageId: result.messageId ?? '?' }));
    },
    onError: (error: Error) => {
      alert(t('clips.shareTelegramFail', { message: error.message }));
    },
  });

  if (variantQuery.isLoading) return <div style={{ padding: 16 }}>{t('common.loading')}</div>;
  if (!data) return <div style={{ padding: 16 }}>{t('variants.notFound')}</div>;

  const { variant, presignedUrl, file, banner, clip, film } = data;
  const title = variantTitle(variant);

  // Способность №1 — из файла самого варианта.
  const canPublish = isAvailable(file);
  // Способность №2 — из foundation-файла КЛИПА. Архивность клипа её не отнимает.
  const clipFile = clip?.file ?? null;
  const canRegenerate = isAvailable(clipFile);

  const publishWhy = canPublish
    ? t('archive.capability.variant.publishOk')
    : isFreed(file)
      ? t('archive.capability.variant.publishNo', { date: shortDate(file.freedAt) })
      : t('archive.capability.variant.publishAbsent');

  const regenerateWhy = canRegenerate
    ? t('archive.capability.variant.regenerateOk')
    : isFreed(clipFile)
      ? t('archive.capability.variant.regenerateNo', { date: shortDate(clipFile?.freedAt ?? null) })
      : t('archive.capability.variant.regenerateAbsent');

  // Модерировать можно только то, что отрендерено.
  const canModerate =
    variant.status === 'ready' || variant.status === 'approved' || variant.status === 'rejected';

  return (
    <>
      <div className="page-head">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h1>{title}</h1>
          <ArchivedChip archivedAt={variant.archivedAt} />
        </div>
        {variant.archivedAt && (
          <div className="filters">
            <button
              type="button"
              onClick={() => unarchiveMutation.mutate()}
              disabled={unarchiveMutation.isPending}
            >
              {t('variants.detail.unarchive')}
            </button>
          </div>
        )}
      </div>

      <div style={{ color: 'var(--text-3)', fontSize: 13, margin: '0 2px 14px' }}>
        <Link to="/variants" style={{ color: 'var(--neon)' }}>
          {t('variants.title')}
        </Link>{' '}
        / {title}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 18 }}>
        <div>
          {isFreed(file) ? (
            <MediaPlaceholder file={file} />
          ) : presignedUrl ? (
            <video
              src={presignedUrl}
              controls
              style={{
                width: '100%',
                maxHeight: 420,
                aspectRatio: '9 / 16',
                background: '#000',
                borderRadius: 10,
              }}
            />
          ) : (
            <div className="player">{t(`clips.variantStatus_${variant.status}`)}</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <button
              type="button"
              className="primary"
              onClick={() => setShowPublish((v) => !v)}
              disabled={!canPublish}
              title={canPublish ? undefined : publishWhy}
            >
              {t('variants.detail.publish')}
            </button>
            {canPublish && presignedUrl ? (
              <a
                href={presignedUrl}
                download={`variant-${variant.id}.mp4`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '7px 12px',
                  fontSize: 12,
                  border: '1px solid var(--line-strong)',
                  borderRadius: 6,
                  color: 'var(--text-2)',
                  textDecoration: 'none',
                }}
              >
                {t('variants.detail.download')}
              </a>
            ) : (
              <button type="button" disabled title={publishWhy}>
                {t('variants.detail.download')}
              </button>
            )}
            <button type="button" onClick={() => setShowArchiveDialog(true)}>
              {t('variants.detail.archive')}
            </button>
          </div>

          {/* Решение по варианту и его пересборка — здесь, а не только в лайтбоксе
              на странице фильма. Перегенерация гаснет ровно тогда, когда об этом
              говорит блок способностей: foundation клипа освобождён. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {canModerate && (
              <>
                <button
                  type="button"
                  onClick={() => moderateMutation.mutate({ status: 'approved' })}
                  disabled={moderateMutation.isPending}
                >
                  ✓ {t('clips.approve')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    moderateMutation.mutate({
                      status: 'rejected',
                      rejectedReason: prompt(t('clips.rejectReasonPrompt')) ?? '',
                    })
                  }
                  disabled={moderateMutation.isPending}
                >
                  ✕ {t('clips.reject')}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => regenerateMutation.mutate()}
              disabled={!canRegenerate || regenerateMutation.isPending}
              title={canRegenerate ? undefined : regenerateWhy}
            >
              ↻ {regenerateMutation.isPending ? '…' : t('variants.detail.regenerate')}
            </button>
          </div>

          {showPublish && canPublish && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                background: 'var(--bg-elev)',
                border: '1px solid var(--line)',
                borderRadius: 8,
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {tgAccounts.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {t('variants.detail.noTelegramAccounts')}
                </span>
              ) : (
                <>
                  <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                    {tgAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.displayName ?? a.handle}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => shareMutation.mutate()}
                    disabled={shareMutation.isPending || !accountId}
                  >
                    {t('clips.shareTelegram')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="section">
            <div className="section-head">
              <h2>{t('variants.detail.capabilities')}</h2>
            </div>
            <div className="section-body caps">
              <CapabilityBlock>
                <Capability
                  available={canPublish}
                  title={t('archive.capability.variant.publish')}
                  why={publishWhy}
                />
                <Capability
                  available={canRegenerate}
                  title={t('archive.capability.variant.regenerate')}
                  why={regenerateWhy}
                />
                <Capability
                  available
                  title={t('archive.capability.variant.standalone')}
                  why={t('archive.capability.variant.standaloneWhy')}
                />
              </CapabilityBlock>
            </div>
          </div>

          <div className="section">
            <div className="section-head">
              <h2>{t('variants.detail.params')}</h2>
            </div>
            <div className="section-body">
              <table className="kv">
                <tbody>
                  <tr>
                    <td>{t('variants.detail.clip')}</td>
                    <td>{clip ? <ParentLink parent={clip} kind="clip" /> : '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('variants.detail.film')}</td>
                    <td>{film ? <ParentLink parent={film} kind="film" /> : '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('variants.detail.banner')}</td>
                    <td>{banner?.name ?? '—'}</td>
                  </tr>
                  {variant.bannerTimingMode === 'freeze' && (
                    <tr>
                      <td>{t('variants.detail.freeze')}</td>
                      <td>
                        ⏸{' '}
                        {t('variants.detail.freezeValue', {
                          at: Number(variant.freezeAtSec ?? 0),
                          dur: Number(variant.freezeDurationSec ?? 0),
                        })}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>{t('variants.detail.size')}</td>
                    <td>{formatBytes(variant.sizeBytes ?? file.sizeBytes) ?? '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('variants.detail.file')}</td>
                    <td>
                      <FileChip file={file} />
                      {file.status === 'absent' && (
                        <span style={{ color: 'var(--text-3)' }}>
                          {t(`clips.variantStatus_${variant.status}`)}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showArchiveDialog && (
        <ArchiveDialog
          kind="variant"
          freeAfterDays={retentionFor('clip_variant')}
          sizeBytes={file.sizeBytes ?? variant.sizeBytes}
          busy={archiveMutation.isPending}
          onCancel={() => setShowArchiveDialog(false)}
          onConfirm={(opts) => archiveMutation.mutate(opts)}
        />
      )}
    </>
  );
}
